<template>
  <div class="card" v-if="hasItem" :class="selectedMediaClass">
    <template v-if="showImage">
      <figure-image :imgset="item.imgset" :caption="item.title" />
    </template>
    <template v-if="mediaType === 'video'">
      <embedded-video :item="item" :auto="auto" :active="active" />
    </template>
    <template v-if="mediaType === 'audio'">
      <audio-widget :item="item" :active="active" :auto="auto" />
    </template>
    <template v-if="mediaType === 'pdf'">
      <iframe :src="fullSrc" class="preview-pdf"></iframe>
    </template>
    <div class="item-info">
      <div v-if="mayEdit" class="control toggle-edit" v-on:click="toggleEdit">
        <b-icon :icon="editIcon" />
      </div>
      <div v-if="mayDelete" class="control delete-item" v-on:click="handleRemove">
        <b-icon icon="trash-can-outline" />
      </div>
      <div v-if="showDelete">
        <p>{{deletePrompt}}</p>
        <b-button type="is-light" @click="cancelDelete">Cancel</b-button>
        <b-button type="is-danger" @click="deleteItem">Delete</b-button>
      </div>

      <div v-if="mayEdit && !editMode" class="add-work-media" @click="addNewMedia">
        <span class="label">Add new media.</span>
        <b-icon icon="plus" size="is-large" />
      </div>
      <div v-if="shortlisted" class="control select-status" :class="selectedClasses" @click="toggleSelected"
        :title="selectToggleHint">
        <b-icon icon="license" size="is-medium" />
      </div>
      <div v-if="!editMode" class="media-info">
        <h4 v-if="hasWork" class="title">
          <span class="label">{{workLabel}}</span>
          <span class="text">{{workTitle}}</span>
        </h4>
        <h4 v-if="hasWork" class="year info-field">
          <span class="label">Year</span>
          <span class="text">{{workYear}}</span>
        </h4>
        <h5 v-if="hasWork" class="medium  info-field" :title="mediaDetails">
          <span class="label">Medium</span>
          <span class="text">{{workMedium}}</span>
        </h5>
        <h5 v-if="hasWorkSize" class="size info-field">
          <span class="label">{{sizeLabel}}</span>
          <span class="text">{{workSize}}</span>
        </h5>
        <div v-if="hasWorkDescription" class="text description  info-field">
          <div class="label">Description</div>
          <p>{{workDescription}}</p>
        </div>
        <article v-if="showStatement" class="text statement info-field">
          <div class="label">Statement</div>
          <div v-html="details.statement"></div>
        </article>
        <div v-if="showEducation" class="text education">
          <p class="education"><span class="college">{{details.college}}</span><span v-if="hasCourse"
              class="course">{{details.course}}</span></p>
        </div>
      </div>
      <work-media-edit v-if="editMode" :item="item" :work="work"></work-media-edit>
    </div>
    <div class="expand-trigger" @click="showFull"></div>
  </div>
</template>
<script>
import { bus } from "../main";
import { mediaItemSchema, workSchema } from "../store/schemas";
import { reviewSubmission } from "../api/methods";
import {
  extractKeyValue,
  hasObjectId,
  notEmptyString,
  expandPath
} from "../api/helpers";
import filters from "../mixins/filters";
import FigureImage from "./FigureImage";
import EmbeddedVideo from "./EmbeddedVideo";
import AudioWidget from "./AudioWidget";
import WorkMediaEdit from "./WorkMediaEdit";

export default {
  name: "MediaCard",
  components: {
    FigureImage,
    EmbeddedVideo,
    AudioWidget,
    WorkMediaEdit
  },
  props: {
    item: {
      type: Object,
      required: true,
      default() {
        return {
          _id: "",
          ...mediaItemSchema
        };
      }
    },
    work: {
      type: Object,
      required: false,
      default() {
        return {
          _id: "",
          ...workSchema
        };
      }
    },
    details: {
      type: Object,
      required: false,
      default() {
        return {
          statement: "",
          course: "",
          college: ""
        };
      }
    },
    selected: {
      type: String,
      default: ""
    },
    subId: {
      type: String,
      default: ""
    },
    auto: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    },
    mayEdit: {
      type: Boolean,
      default: false
    },
    showExtra: {
      type: Boolean,
      default: true
    }
  },
  mixins: [filters],
  data() {
    return {
      editMode: false,
      updating: false,
      refresh: false,
      showDelete: false,
      deletePrompt: "",
      shortlistedVal: false
    };
  },
  created() {
    const { submission } = this.$parent;
    if (submission) {
      this.shortlistedVal =
        submission.shortlisted || submission.hasSelectedWork;
    }
    bus.$on("media-item-updated", item => {
      if (item instanceof Object) {
        setTimeout(() => {
          this.$forceUpdate();
        }, 500);
      }
    });
    bus.$on("work-updated", (work, subKey, subVal) => {
      if (work instanceof Object) {
        if (subKey) {
          switch (subKey) {
            case "shortlisted":
              this.shortlistedVal = subVal;
              this.$parent.submission.shortlisted = subVal;
              break;
            case "selected":
              if (work._id === this.work._id) {
                this.work.selected = subVal;
                if (this.$parent.submission) {
                  const wi = this.$parent.submission.works.findIndex(
                    w => w._id === work._id
                  );
                  if (wi >= 0) {
                    let w = this.$parent.submission.works[wi];
                    w.selected = subVal;
                    this.$parent.submission.works.splice(wi, 1, w);
                    this.$parent.submission.hasSelectedWork =
                      this.$parent.submission.works.filter(w => w.selected)
                        .length > 0;
                  }
                }
              }
              break;
          }
        }
        setTimeout(() => {
          this.$forceUpdate();
        }, 400);
      }
    });
    bus.$on("escape", val => {
      if (val) {
        this.editMode = false;
      }
    });
  },
  watch: {
    "item.filename"(cv, pv) {
      if (cv !== pv && notEmptyString(pv, 5) && notEmptyString(cv, 5)) {
        this.refresh == true;
        setTimeout(() => {
          this.refresh == false;
        }, 250);
      }
    },
    "item._id"(cv, pv) {
      if (cv !== pv && notEmptyString(pv, 5) && notEmptyString(cv, 5)) {
        this.editMode = false;
      }
    }
  },
  computed: {
    hasItem() {
      return (hasObjectId(this.item) && extractKeyValue(this.item, "filename", "").length > 4) || this.hasWorkDescription;
    },
    hasEducation() {
       if (this.details instanceof Object) {
         const { college }  = this.details;
         return notEmptyString(college);
       } else {
         return false;
       }
    },
    showEducation() {
       return this.showExtra && this.hasEducation;
    },
    hasCourse() {
       if (this.details instanceof Object) {
         const { course } = this.details;
         return notEmptyString(course);
       } else {
         return false;
       }
    },
    fullSrc() {
      let src = "";
      if (this.item.filename) {
        src = expandPath(this.item.filename);
      }
      return src;
    },
    mediaType() {
      let type = "";
      if (this.item.type) {
        type = this.item.type;
      } else {
        switch (this.item.mime) {
          case "application/pdf":
            type = "pdf";
            break;
          case "image/jpeg":
          case "image/jpg":
          case "image/gif":
          case "image/png":
            type = "image";
            break;
        }
      }
      return type;
    },
    mediaTitle() {
      let str = "";
      if (this.hasItem) {
        str = this.item.title;
      }
      return str;
    },
    hasWork() {
      return (
        hasObjectId(this.work) &&
        extractKeyValue(this.work, "title", "").length > 1
      );
    },
    mayDelete() {
      return this.mayEdit && this.hasWork && !this.work.selected;
    },
    showSelected() {
      const { path } = this.$route;
      return this.work.selected && path.indexOf("/submissions/") === 0;
    },
    shortlisted() {
      return this.shortlistedVal;
    },
    selectToggleHint() {
      return this.work.selected
        ? "Remove work from shortlist"
        : "Shortlist work";
    },
    selectedClasses() {
      let cls = ["status"];
      cls.push(this.work.selected ? "selected" : "unselected");
      return cls;
    },
    workTitle() {
      return this.hasWork ? this.work.title : "";
    },
    workNumber() {
      let n = 1;
      if (this.work.index) {
        n = this.work.index + 1;
      }
      return n;
    },
    workLabel() {
      return this.hasWork ? ["Work", this.workNumber].join(" ") : "";
    },
    workMedium() {
      return this.hasWork ? this.work.medium : "";
    },
    workYear() {
      return this.hasWork ? this.work.year.toString() : "";
    },
    workSize() {
      return this.hasWork ? this.work.size : "";
    },
    hasWorkSize() {
      return this.hasWork ? this.work.size.trim().length > 1 : false;
    },
    sizeLabel() {
      let str = "Size";
      if (this.hasWorkSize) {
        if (/^\s*\d+:[0-5]?\d(:[0-5]?\d)?\s*$/.test(this.workSize)) {
          str = "Duration";
        } else if (/^\s*\d.*?\d.*?\d/.test(this.workSize)) {
          str = "Dimensions";
        }
      }
      return str;
    },
    workDescription() {
      return this.hasWork ? this.work.description : "";
    },
    hasWorkDescription() {
      return this.hasWork ? notEmptyString(this.work.description, 3) : false;
    },
    selectedMediaClass() {
      const selCls =
        this.item._id === this.selected ? "selected" : "show-inline";
      let cls = [selCls, this.item.type];
      if (!this.auto) {
        cls.push("preview-mode");
      }
      if (this.updating) {
        cls.push("updating");
      }
      if (this.work.selected) {
        cls.push("selected-work");
      }
      return cls;
    },
    showMediaInfo() {
      switch (this.type) {
        case "intro":
        case "text":
          return false;
        default:
          return true;
      }
    },
    showWork() {
      switch (this.type) {
        case "intro":
          return false;
        default:
          return true;
      }
    },
    showExpand() {
      return this.showWork && !this.auto;
    },
    showStatement() {
      return notEmptyString(this.details.statement, 5);
    },
    mediaDetails() {
      let parts = [];
      switch (this.item.type) {
        case "image":
          if (this.item.attributes instanceof Object) {
            const { width, height } = this.item.attributes;
            parts.push(`${width}x${height} px`);
          }
          break;
        case "video":
          if (this.item.attributes instanceof Object) {
            const { duration } = this.item.attributes;
            parts.push(this.secondsToHMS(duration));
          }
          break;
      }
      if (this.item.size) {
        parts.push(this.formatFileSize(this.item.size));
      }
      return parts.join(", ");
    },
    editIcon() {
      return this.editMode ? "close" : "square-edit-outline";
    },
    showImage() {
      return this.item.type === "image" && !this.refresh;
    }
  },
  methods: {
    showFull() {
      if (this.$parent.showFull) {
        if (typeof this.$parent.showFull === "function") {
          const wid = notEmptyString(this.work._id, 12)
            ? this.work._id
            : "";
          this.$parent.showFull(this.item, wid);
        }
      }
    },
    toggleEdit() {
      this.editMode = this.mayEdit ? !this.editMode : false;
      const { path } = this.$route;
      if (/submissions\//.test(path)) {
        const itemRef = notEmptyString(this.item._id, 5)
          ? this.item._id
          : 'new'
        let newPath = "/submissions/" + this.subId + "/" + itemRef;
        if (this.editMode) {
          newPath += "/edit";
        }
        if (newPath !== path) {
          this.$router.push({
            path: newPath,
            query: this.$route.query
          });
        }
      }
    },
    handleRemove() {
      if (this.$parent.submission) {
        if (this.$parent.submission.works instanceof Array) {
          const { works } = this.$parent.submission;
          const work = works.find(w => w._id === this.work._id);
          if (work instanceof Object) {
            const numMedia =
              work.mediaItems instanceof Array ? work.mediaItems.length : 0;
            let prompt = "";
            //let mode = "none";
            if (numMedia > 1) {
              prompt = "Do you want to delete this media item?";
              //mode = "media";
            } else {
              //mode = "work";
              prompt = "Do you want to delete this work";
              if (numMedia > 0) {
                prompt += " and related media";
              }
            }
            this.showDelete = true;
            this.deletePrompt = prompt;
          }
        }
      }
    },
    deleteItem() {
      bus.$emit("delete-media-item", {
        subId: this.subId,
        workId: this.work._id,
        itemId: this.item._id
      });
    },
    cancelDelete() {
      this.deletePrompt = "";
      this.showDelete = false;
    },
    toggleSelected() {
      const newVal = this.work.selected !== true;
      reviewSubmission(this.subId, "selected", newVal, this.work._id).then(
        data => {
          if (data.submission) {
            if (data.submission instanceof Object) {
              const work = data.submission.works.find(
                w => w._id === this.work._id
              );
              if (work) {
                bus.$emit("update-work", {
                  subId: this.subId,
                  workId: work._id,
                  work
                });
              }
            }
          }
        }
      );
    },
    addNewMedia() {
      if (this.$parent.addNew instanceof Function) {
        this.$parent.addNew(this.work._id);
      }
    }
  }
};
</script>